// Synthesised sound effects + a tiny chiptune tracker. No audio files needed.

const NOTE_IDX = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
function midi(n) {
  const m = /^([A-G])([#b]?)(-?\d)$/.exec(n);
  if (!m) return 60;
  let v = NOTE_IDX[m[1]] + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0);
  return v + (parseInt(m[3], 10) + 1) * 12;
}
const mtof = (m) => 440 * Math.pow(2, (m - 69) / 12);
const nf = (n) => mtof(midi(n));

// chord name -> midi notes (root position around octave 3/4)
function chordNotes(name, octave = 3) {
  const m = /^([A-G][#b]?)(m|7|maj7|m7|dim|sus4|sus2)?$/.exec(name);
  const root = midi(m[1] + octave);
  const q = m[2] || '';
  let iv = [0, 4, 7];
  if (q === 'm') iv = [0, 3, 7];
  else if (q === '7') iv = [0, 4, 7, 10];
  else if (q === 'maj7') iv = [0, 4, 7, 11];
  else if (q === 'm7') iv = [0, 3, 7, 10];
  else if (q === 'dim') iv = [0, 3, 6];
  else if (q === 'sus4') iv = [0, 5, 7];
  else if (q === 'sus2') iv = [0, 2, 7];
  return iv.map((i) => root + i);
}

function parseMelody(str) {
  // "C5:2 r:2 E5:4" -> [{step, midi, len}]
  const out = [];
  let step = 0;
  for (const tok of str.trim().split(/\s+/)) {
    const [n, l] = tok.split(':');
    const len = parseFloat(l || '1');
    if (n !== 'r') out.push({ step, midi: midi(n), len });
    step += len;
  }
  return { notes: out, length: step };
}

const DRUMS = {
  pop: ['k.h.s.h.k.h.s.hh', 'k.h.s.h.kkh.s.ho'],
  drive: ['kh.hskhhk.khs.hh', 'kh.hskhhkhkhs.ss'],
  gentle: ['k...h...k...h...', 'k...h...k.h.h...'],
  march: ['k.hhs.hhk.hhs.hh', 'k.hhs.hhkkhhs.ss'],
  synth: ['k.h.s.hkk.h.s.h.', 'k.h.s.hkk.h.ssss'],
  none: ['................'],
};

const TRACKS = {
  title: {
    bpm: 116, lead: 'square', cutoff: 2600, bass: 'pop', drums: 'pop', arp: true,
    chords: ['C', 'G', 'Am', 'F', 'C', 'G', 'F', 'G'],
    melody:
      'G4:2 C5:2 E5:2 G5:4 E5:2 C5:2 E5:2 D5:4 B4:2 G4:2 B4:2 D5:2 G5:4 ' +
      'A4:2 C5:2 E5:2 A5:4 G5:2 E5:2 C5:2 F5:4 E5:2 D5:2 C5:4 A4:4 ' +
      'G4:2 C5:2 E5:2 G5:4 A5:2 G5:2 E5:2 D5:2 E5:2 F5:2 G5:4 F5:2 E5:2 D5:2 ' +
      'C5:2 D5:2 E5:2 F5:4 A5:4 G5:2 G5:6 F5:2 E5:2 D5:2 B4:4',
  },
  plains: {
    bpm: 108, lead: 'square', cutoff: 2200, bass: 'pop', drums: 'pop', arp: true,
    chords: ['F', 'C', 'Dm', 'Bb', 'F', 'C', 'Bb', 'C'],
    melody:
      'C5:2 F5:2 A5:2 F5:2 G5:2 A5:2 C6:4 Bb5:2 A5:2 G5:2 E5:2 C5:4 r:4 ' +
      'D5:2 F5:2 A5:2 D6:4 C6:2 A5:2 F5:2 G5:4 F5:2 D5:2 Bb4:4 r:4 ' +
      'A5:2 G5:2 F5:2 A5:2 C6:4 A5:4 G5:2 E5:2 C5:2 E5:2 G5:4 Bb5:4 ' +
      'A5:2 Bb5:2 A5:2 G5:2 F5:4 D5:4 E5:4 G5:4 F5:8',
  },
  desert: {
    bpm: 100, lead: 'sawtooth', cutoff: 1800, bass: 'walk', drums: 'march', arp: false,
    chords: ['Dm', 'Dm', 'A', 'A', 'Gm', 'Dm', 'A', 'A'],
    melody:
      'D5:4 E5:2 F5:2 A5:4 G5:2 F5:2 E5:2 F5:2 E5:2 D5:2 C#5:4 D5:4 ' +
      'A4:2 Bb4:2 C#5:2 E5:2 G5:4 F5:2 E5:2 F5:2 E5:2 D5:2 C#5:2 A4:8 ' +
      'G5:4 Bb5:2 A5:2 G5:4 F5:2 E5:2 F5:2 G5:2 A5:4 D5:4 F5:4 ' +
      'E5:2 F5:2 G5:2 A5:2 Bb5:4 A5:4 G5:2 F5:2 E5:2 C#5:2 D5:8',
  },
  snow: {
    bpm: 92, lead: 'bell', cutoff: 5000, bass: 'sparse', drums: 'gentle', arp: true,
    chords: ['D', 'Bm', 'G', 'A', 'D', 'F#m', 'G', 'A'],
    melody:
      'F#5:4 A5:4 D6:4 C#6:2 B5:2 B5:6 A5:2 F#5:4 D5:4 ' +
      'G5:4 B5:4 D6:4 E6:2 D6:2 C#6:8 A5:4 r:4 ' +
      'A5:2 B5:2 A5:2 F#5:2 D5:4 F#5:4 C#6:4 A5:4 F#5:4 A5:4 ' +
      'B5:4 D6:4 G6:4 F#6:2 E6:2 E6:4 C#6:4 A5:8',
  },
  lava: {
    bpm: 132, lead: 'sawtooth', cutoff: 2000, bass: 'drive', drums: 'drive', arp: false,
    chords: ['Am', 'Am', 'F', 'G', 'Am', 'Am', 'F', 'E'],
    melody:
      'A4:2 A4:1 C5:1 E5:2 A4:2 G5:2 E5:2 C5:2 E5:2 D5:2 C5:2 B4:2 C5:2 A4:4 E4:4 ' +
      'F4:2 A4:2 C5:2 F5:2 E5:2 C5:2 A4:2 C5:2 D5:2 B4:2 G4:2 B4:2 D5:4 G5:4 ' +
      'E5:2 A5:2 E5:2 C5:2 A4:2 C5:2 E5:4 G5:2 F5:2 E5:2 D5:2 C5:2 B4:2 A4:4 ' +
      'A4:2 C5:2 F5:4 E5:2 D5:2 C5:4 B4:4 G#4:4 E4:4 G#4:4',
  },
  glitch: {
    bpm: 120, lead: 'square', cutoff: 3200, bass: 'drive', drums: 'synth', arp: true,
    chords: ['Cm', 'Ab', 'Bb', 'Gm', 'Cm', 'Ab', 'Bb', 'G'],
    melody:
      'C5:4 Eb5:2 G5:2 C6:4 Bb5:2 G5:2 Ab5:6 G5:2 Eb5:4 C5:4 ' +
      'D5:2 F5:2 Bb5:4 C6:2 Bb5:2 D6:4 G5:4 F5:2 D5:2 Bb4:4 D5:4 ' +
      'G5:2 C6:2 Eb6:4 D6:2 C6:2 G5:4 Ab5:4 C6:4 Eb6:4 C6:4 ' +
      'D6:4 Bb5:2 F5:2 D5:4 F5:4 G5:4 B5:4 D6:4 B5:4',
  },
  boss: {
    bpm: 144, lead: 'sawtooth', cutoff: 2400, bass: 'drive', drums: 'drive', arp: true,
    chords: ['Dm', 'Dm', 'Bb', 'C', 'Dm', 'Dm', 'Bb', 'A'],
    melody:
      'D5:1 D5:1 r:2 D5:1 D5:1 r:2 F5:2 E5:2 D5:2 A4:2 F5:2 G5:2 A5:4 G5:2 F5:2 E5:4 ' +
      'Bb4:2 D5:2 F5:2 Bb5:4 A5:2 G5:2 F5:2 E5:2 G5:2 C6:4 Bb5:2 A5:2 G5:4 ' +
      'A5:2 D6:2 A5:2 F5:2 D5:2 F5:2 A5:4 G5:2 F5:2 E5:2 F5:2 D5:8 ' +
      'F5:2 Bb5:2 D6:4 C6:2 Bb5:2 A5:4 E5:2 F5:2 G5:2 A5:2 C#6:4 A5:4',
  },
  blitz: {
    bpm: 150, lead: 'square', cutoff: 3000, bass: 'drive', drums: 'pop', arp: true,
    chords: ['G', 'Em', 'C', 'D', 'G', 'Em', 'C', 'D'],
    melody:
      'G5:2 B5:2 D6:2 B5:2 G5:2 A5:2 B5:4 E5:2 G5:2 B5:2 G5:2 E5:2 F#5:2 G5:4 ' +
      'C6:2 B5:2 A5:2 G5:2 E5:2 G5:2 C6:4 D6:4 C6:2 B5:2 A5:4 F#5:4 ' +
      'D6:2 B5:2 G5:2 B5:2 D6:2 E6:2 D6:4 B5:2 G5:2 E5:2 G5:2 B5:2 C6:2 B5:4 ' +
      'A5:2 G5:2 E5:2 G5:2 A5:2 B5:2 C6:4 B5:2 A5:2 F#5:2 A5:2 D6:8',
  },
  island: {
    bpm: 96, lead: 'triangle', cutoff: 4000, bass: 'sparse', drums: 'gentle', arp: true,
    chords: ['C', 'Am', 'F', 'G', 'C', 'Em', 'F', 'G'],
    melody:
      'E5:4 G5:4 C6:6 B5:2 A5:4 C6:4 E5:8 F5:4 A5:4 C6:4 A5:4 G5:8 D5:8 ' +
      'E5:4 G5:4 C6:4 D6:4 B5:8 G5:8 A5:4 C6:4 F5:4 A5:4 G5:12 r:4',
  },
};

export class AudioEngine {
  constructor() {
    this.ctx = null;
    this.musicVol = 0.55;
    this.sfxVol = 0.8;
    this.current = null;
    this.pendingTrack = null;
    this.duck = 1;
    this.lastPlay = {};
    this.voice = null;
  }

  unlock() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC({ latencyHint: 'interactive' });
      const c = this.ctx;
      this.master = c.createGain();
      this.master.gain.value = 0.9;
      const comp = c.createDynamicsCompressor();
      comp.threshold.value = -14;
      comp.ratio.value = 4;
      this.master.connect(comp).connect(c.destination);
      this.music = c.createGain();
      this.music.gain.value = this.musicVol * 0.5;
      this.music.connect(this.master);
      this.sfx = c.createGain();
      this.sfx.gain.value = this.sfxVol;
      this.sfx.connect(this.master);
      // reverb send
      this.reverb = c.createConvolver();
      this.reverb.buffer = this.impulse(1.8, 2.5);
      this.revSend = c.createGain();
      this.revSend.gain.value = 0.35;
      this.revSend.connect(this.reverb).connect(this.master);
      // noise buffer
      const len = c.sampleRate * 2;
      this.noiseBuf = c.createBuffer(1, len, c.sampleRate);
      const d = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      document.addEventListener('visibilitychange', () => {
        if (!this.ctx) return;
        if (document.hidden) this.ctx.suspend();
        else this.ctx.resume();
      });
      if (this.pendingTrack) {
        const t = this.pendingTrack;
        this.pendingTrack = null;
        this.playMusic(t);
      }
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
  }

  impulse(sec, decay) {
    const c = this.ctx;
    const len = Math.floor(c.sampleRate * sec);
    const buf = c.createBuffer(2, len, c.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
    return buf;
  }

  setVolumes(music, sfx) {
    this.musicVol = music;
    this.sfxVol = sfx;
    if (!this.ctx) return;
    this.music.gain.setTargetAtTime(music * 0.5 * this.duck, this.ctx.currentTime, 0.05);
    this.sfx.gain.setTargetAtTime(sfx, this.ctx.currentTime, 0.05);
  }

  setDuck(on) {
    this.duck = on ? 0.35 : 1;
    if (this.ctx) this.music.gain.setTargetAtTime(this.musicVol * 0.5 * this.duck, this.ctx.currentTime, 0.25);
  }

  // ---------- primitives ----------
  tone(t, { type = 'square', f = 440, f2 = null, dur = 0.1, vol = 0.2, a = 0.005, r = null, cutoff = null, q = 1, dest = null, rev = 0, detune = 0, glide = null }) {
    const c = this.ctx;
    const o = c.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(f, t);
    if (detune) o.detune.value = detune;
    if (f2 !== null) o.frequency.exponentialRampToValueAtTime(Math.max(20, f2), t + (glide ?? dur));
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + a);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + (r ?? 0.02));
    let node = o;
    if (cutoff) {
      const fl = c.createBiquadFilter();
      fl.type = 'lowpass';
      fl.frequency.value = cutoff;
      fl.Q.value = q;
      node.connect(fl);
      node = fl;
    }
    node.connect(g);
    g.connect(dest || this.sfx);
    if (rev) {
      const s = c.createGain();
      s.gain.value = rev;
      g.connect(s).connect(this.revSend);
    }
    o.start(t);
    o.stop(t + dur + (r ?? 0.02) + 0.05);
    return o;
  }

  noise(t, { dur = 0.1, vol = 0.2, type = 'bandpass', f = 1000, f2 = null, q = 1, a = 0.003, dest = null, rev = 0 }) {
    const c = this.ctx;
    const s = c.createBufferSource();
    s.buffer = this.noiseBuf;
    const fl = c.createBiquadFilter();
    fl.type = type;
    fl.frequency.setValueAtTime(f, t);
    if (f2) fl.frequency.exponentialRampToValueAtTime(f2, t + dur);
    fl.Q.value = q;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + a);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(fl).connect(g).connect(dest || this.sfx);
    if (rev) {
      const sg = c.createGain();
      sg.gain.value = rev;
      g.connect(sg).connect(this.revSend);
    }
    s.start(t, Math.random() * 1.5);
    s.stop(t + dur + 0.05);
  }

  arp(t, notes, step, opts) {
    notes.forEach((n, i) => this.tone(t + i * step, { f: typeof n === 'number' ? n : nf(n), ...opts }));
  }

  // ---------- sound effects ----------
  play(name, opt = {}) {
    if (!this.ctx || this.ctx.state !== 'running') return;
    const now = this.ctx.currentTime;
    // rate-limit identical sounds
    const minGap = { step: 0.06, coin: 0.03, build: 0.03 }[name.split('_')[0]] ?? 0.02;
    if (this.lastPlay[name] && now - this.lastPlay[name] < minGap) return;
    this.lastPlay[name] = now;
    const t = now + 0.005;
    const p = opt.pitch ?? 1;
    switch (name) {
      case 'jump':
        this.tone(t, { type: 'square', f: 280 * p, f2: 560 * p, dur: 0.1, vol: 0.08, cutoff: 2400 });
        break;
      case 'doubleJump':
        this.tone(t, { type: 'square', f: 420, f2: 980, dur: 0.12, vol: 0.08, cutoff: 3000 });
        this.noise(t, { dur: 0.15, vol: 0.06, f: 3000, f2: 6000, q: 0.7 });
        break;
      case 'land':
        this.noise(t, { dur: 0.09, vol: 0.18 * p, type: 'lowpass', f: 500, q: 0.7 });
        break;
      case 'step_grass':
        this.noise(t, { dur: 0.05, vol: 0.05, f: 1300 * p, q: 1.2 });
        break;
      case 'step_stone':
        this.noise(t, { dur: 0.035, vol: 0.06, f: 2600 * p, q: 2 });
        this.tone(t, { type: 'triangle', f: 160 * p, dur: 0.03, vol: 0.04 });
        break;
      case 'step_wood':
        this.tone(t, { type: 'triangle', f: 210 * p, f2: 150, dur: 0.05, vol: 0.08 });
        this.noise(t, { dur: 0.03, vol: 0.03, f: 1800, q: 2 });
        break;
      case 'step_sand':
        this.noise(t, { dur: 0.07, vol: 0.05, type: 'highpass', f: 2800 * p, q: 0.7 });
        break;
      case 'step_snow':
        this.noise(t, { dur: 0.08, vol: 0.06, f: 1600 * p, q: 0.8 });
        break;
      case 'step_metal':
        this.tone(t, { type: 'square', f: 900 * p, dur: 0.03, vol: 0.02, cutoff: 2500 });
        this.noise(t, { dur: 0.03, vol: 0.04, f: 3500, q: 3 });
        break;
      case 'step_glass':
        this.tone(t, { type: 'sine', f: 1900 * p, dur: 0.04, vol: 0.03 });
        this.noise(t, { dur: 0.03, vol: 0.03, f: 4000, q: 3 });
        break;
      case 'step_slime':
        this.tone(t, { type: 'sine', f: 240 * p, f2: 120, dur: 0.08, vol: 0.08 });
        break;
      case 'coin': {
        const k = Math.pow(2, Math.min(opt.chain || 0, 12) / 24);
        this.tone(t, { type: 'square', f: 988 * k, dur: 0.06, vol: 0.07, cutoff: 5000 });
        this.tone(t + 0.06, { type: 'square', f: 1319 * k, dur: 0.22, vol: 0.07, cutoff: 5000, r: 0.15 });
        break;
      }
      case 'gem':
        this.arp(t, ['C6', 'E6', 'G6', 'C7'], 0.05, { type: 'triangle', dur: 0.18, vol: 0.12, rev: 0.6 });
        this.tone(t, { type: 'sine', f: 2093, dur: 0.5, vol: 0.04, rev: 0.8 });
        break;
      case 'star':
        this.arp(t, ['C5', 'E5', 'G5', 'C6', 'E6', 'G6', 'C7'], 0.06, { type: 'triangle', dur: 0.25, vol: 0.12, rev: 0.7 });
        this.arp(t + 0.05, ['G5', 'C6', 'E6', 'G6'], 0.09, { type: 'sine', dur: 0.4, vol: 0.06, rev: 0.9 });
        break;
      case 'swing':
        this.noise(t, { dur: 0.13, vol: 0.12, f: 700, f2: 3200, q: 1.2 });
        break;
      case 'hit':
        this.tone(t, { type: 'square', f: 180, f2: 70, dur: 0.08, vol: 0.12, cutoff: 1500 });
        this.noise(t, { dur: 0.07, vol: 0.18, f: 1500, q: 0.8 });
        break;
      case 'mobDie':
        this.tone(t, { type: 'square', f: 700, f2: 180, dur: 0.18, vol: 0.08, cutoff: 2500 });
        this.noise(t + 0.02, { dur: 0.2, vol: 0.15, f: 900, f2: 300, q: 0.8 });
        this.tone(t + 0.12, { type: 'triangle', f: 1046, dur: 0.12, vol: 0.06 });
        break;
      case 'squish':
        this.tone(t, { type: 'sine', f: 420, f2: 90, dur: 0.16, vol: 0.18, cutoff: 1200 });
        this.noise(t, { dur: 0.1, vol: 0.08, type: 'lowpass', f: 800 });
        break;
      case 'hurt':
        this.tone(t, { type: 'sawtooth', f: 260, f2: 120, dur: 0.25, vol: 0.12, cutoff: 1400 });
        this.noise(t, { dur: 0.12, vol: 0.1, f: 600, q: 0.8 });
        break;
      case 'checkpoint':
        this.arp(t, ['G5', 'B5', 'D6', 'G6'], 0.07, { type: 'triangle', dur: 0.22, vol: 0.12, rev: 0.6 });
        this.arp(t, ['G4', 'D5'], 0.0, { type: 'square', dur: 0.4, vol: 0.04, cutoff: 1800 });
        break;
      case 'correct':
        this.arp(t, ['C5', 'E5', 'G5', 'C6'], 0.065, { type: 'square', dur: 0.14, vol: 0.07, cutoff: 3500 });
        this.arp(t, ['C5', 'E5', 'G5', 'C6'], 0.065, { type: 'triangle', dur: 0.2, vol: 0.1, rev: 0.4 });
        this.tone(t + 0.28, { type: 'sine', f: 2093, dur: 0.35, vol: 0.05, rev: 0.8 });
        break;
      case 'wrong':
        this.tone(t, { type: 'triangle', f: 330, dur: 0.14, vol: 0.14 });
        this.tone(t + 0.16, { type: 'triangle', f: 262, f2: 220, dur: 0.3, vol: 0.14 });
        break;
      case 'build':
        this.noise(t, { dur: 0.06, vol: 0.14, f: 900 * p, q: 1.5 });
        this.tone(t, { type: 'triangle', f: 200 * p, f2: 120 * p, dur: 0.07, vol: 0.12 });
        break;
      case 'break':
        this.noise(t, { dur: 0.16, vol: 0.2, f: 1100 * p, f2: 400, q: 0.9 });
        this.noise(t + 0.03, { dur: 0.1, vol: 0.1, f: 3000, q: 2 });
        break;
      case 'bounce':
        this.tone(t, { type: 'sine', f: 180, f2: 760, dur: 0.16, glide: 0.12, vol: 0.2 });
        this.tone(t + 0.12, { type: 'triangle', f: 760, f2: 520, dur: 0.15, vol: 0.06 });
        break;
      case 'speed':
        this.noise(t, { dur: 0.35, vol: 0.1, f: 600, f2: 4000, q: 2 });
        this.tone(t, { type: 'sawtooth', f: 200, f2: 800, dur: 0.3, vol: 0.05, cutoff: 2000 });
        break;
      case 'lucky':
        for (let i = 0; i < 6; i++) this.tone(t + i * 0.05, { type: 'square', f: 600 + i * 120, dur: 0.04, vol: 0.05, cutoff: 3000 });
        this.arp(t + 0.32, ['E6', 'G6', 'C7'], 0.06, { type: 'triangle', dur: 0.25, vol: 0.12, rev: 0.6 });
        break;
      case 'powerup':
        this.arp(t, ['C5', 'G5', 'C6', 'E6', 'G6', 'C7'], 0.05, { type: 'square', dur: 0.1, vol: 0.06, cutoff: 4000, rev: 0.4 });
        break;
      case 'heart':
        this.arp(t, ['E5', 'A5', 'C#6'], 0.08, { type: 'triangle', dur: 0.2, vol: 0.12, rev: 0.5 });
        break;
      case 'portal':
        this.tone(t, { type: 'sine', f: 180, f2: 1400, dur: 1.1, vol: 0.12, rev: 0.9 });
        this.tone(t, { type: 'triangle', f: 270, f2: 2100, dur: 1.1, vol: 0.05, rev: 0.9 });
        this.noise(t, { dur: 1.0, vol: 0.08, f: 500, f2: 6000, q: 3, rev: 0.8 });
        break;
      case 'whoosh':
        this.noise(t, { dur: 0.45, vol: 0.14, f: 300, f2: 2400, q: 1.5 });
        break;
      case 'splash':
        this.noise(t, { dur: 0.35, vol: 0.2, type: 'lowpass', f: 2500, f2: 300 });
        break;
      case 'levelComplete': {
        const n = ['C5', 'E5', 'G5', 'C6', 'G5', 'C6'];
        const d = [0.12, 0.12, 0.12, 0.25, 0.12, 0.6];
        let tt = t;
        n.forEach((x, i) => {
          this.tone(tt, { type: 'square', f: nf(x), dur: d[i], vol: 0.08, cutoff: 3500, rev: 0.3 });
          this.tone(tt, { type: 'triangle', f: nf(x) / 2, dur: d[i], vol: 0.1 });
          tt += d[i] + 0.02;
        });
        this.arp(tt - 0.5, ['C6', 'E6', 'G6', 'C7'], 0.07, { type: 'sine', dur: 0.4, vol: 0.06, rev: 0.9 });
        break;
      }
      case 'levelUp':
        this.arp(t, ['C5', 'E5', 'G5', 'C6', 'E6', 'G6', 'C7'], 0.07, { type: 'square', dur: 0.15, vol: 0.07, cutoff: 4000, rev: 0.5 });
        this.arp(t + 0.5, ['C6', 'G6', 'C7'], 0.0, { type: 'triangle', dur: 0.8, vol: 0.06, rev: 0.8 });
        break;
      case 'click':
        this.tone(t, { type: 'square', f: 880, dur: 0.03, vol: 0.05, cutoff: 3000 });
        break;
      case 'hover':
        this.tone(t, { type: 'sine', f: 1400, dur: 0.02, vol: 0.02 });
        break;
      case 'pop':
        this.tone(t, { type: 'sine', f: 500, f2: 1200, dur: 0.07, vol: 0.12 });
        break;
      case 'buy':
        for (let i = 0; i < 5; i++) this.tone(t + i * 0.06, { type: 'square', f: 988 + i * 140, dur: 0.06, vol: 0.05, cutoff: 5000 });
        this.arp(t + 0.3, ['G6', 'C7'], 0.08, { type: 'triangle', dur: 0.3, vol: 0.1, rev: 0.6 });
        break;
      case 'explosion':
        this.noise(t, { dur: 0.9, vol: 0.5, type: 'lowpass', f: 1800, f2: 80, q: 0.7 });
        this.tone(t, { type: 'sine', f: 90, f2: 30, dur: 0.6, vol: 0.4 });
        break;
      case 'fuse':
        this.noise(t, { dur: 1.1, vol: 0.06, type: 'highpass', f: 4500, q: 0.5 });
        break;
      case 'bossRoar':
        this.tone(t, { type: 'sawtooth', f: 110, f2: 55, dur: 1.0, vol: 0.18, cutoff: 700, q: 4 });
        this.tone(t, { type: 'square', f: 82, f2: 41, dur: 1.0, vol: 0.1, cutoff: 500 });
        this.noise(t, { dur: 0.9, vol: 0.12, type: 'lowpass', f: 900, f2: 200 });
        break;
      case 'bossHit':
        this.noise(t, { dur: 0.4, vol: 0.35, f: 800, f2: 200, q: 0.8 });
        this.tone(t, { type: 'square', f: 140, f2: 60, dur: 0.35, vol: 0.18, cutoff: 1200 });
        this.tone(t, { type: 'sine', f: 1760, f2: 1500, dur: 0.6, vol: 0.05, rev: 0.8 });
        break;
      case 'bossLaugh':
        for (let i = 0; i < 4; i++) this.tone(t + i * 0.13, { type: 'sawtooth', f: 260 - i * 15, f2: 200 - i * 15, dur: 0.1, vol: 0.09, cutoff: 1200, q: 3 });
        break;
      case 'zap':
        this.noise(t, { dur: 0.3, vol: 0.2, type: 'highpass', f: 2500, q: 0.6 });
        this.tone(t, { type: 'sawtooth', f: 2200, f2: 160, dur: 0.32, vol: 0.12, cutoff: 5000 });
        break;
      case 'stomp':
        this.noise(t, { dur: 0.35, vol: 0.4, type: 'lowpass', f: 400, f2: 60 });
        this.tone(t, { type: 'sine', f: 70, f2: 35, dur: 0.3, vol: 0.4 });
        break;
      case 'countdown':
        this.tone(t, { type: 'square', f: 660, dur: 0.1, vol: 0.08, cutoff: 3000 });
        break;
      case 'go':
        this.tone(t, { type: 'square', f: 990, dur: 0.35, vol: 0.09, cutoff: 3500, rev: 0.4 });
        break;
      case 'tick':
        this.tone(t, { type: 'square', f: 1500, dur: 0.015, vol: 0.03 });
        break;
      case 'achievement':
        this.arp(t, ['E6', 'G#6', 'B6', 'E7'], 0.08, { type: 'triangle', dur: 0.3, vol: 0.12, rev: 0.7 });
        this.arp(t, ['E5', 'B5'], 0.0, { type: 'square', dur: 0.5, vol: 0.04, cutoff: 2500 });
        break;
      case 'chest':
        this.tone(t, { type: 'sawtooth', f: 120, f2: 240, dur: 0.35, vol: 0.06, cutoff: 900, q: 6 });
        this.arp(t + 0.3, ['C6', 'E6', 'G6', 'C7', 'E7'], 0.05, { type: 'square', dur: 0.08, vol: 0.05, cutoff: 5000, rev: 0.4 });
        break;
      case 'combo': {
        const s = Math.min(opt.streak || 1, 20);
        this.tone(t, { type: 'triangle', f: mtof(72 + s), dur: 0.15, vol: 0.1, rev: 0.4 });
        break;
      }
      case 'groan':
        this.tone(t, { type: 'sawtooth', f: 150, f2: 105, dur: 0.55, vol: 0.05, cutoff: 600, q: 5 });
        this.tone(t + 0.05, { type: 'sine', f: 120, f2: 90, dur: 0.5, vol: 0.05 });
        break;
      case 'respawn':
        this.arp(t, ['G4', 'C5', 'E5'], 0.06, { type: 'triangle', dur: 0.15, vol: 0.1, rev: 0.5 });
        break;
      case 'dash':
        this.noise(t, { dur: 0.18, vol: 0.12, f: 1200, f2: 5000, q: 1 });
        break;
      case 'crumble':
        this.noise(t, { dur: 0.25, vol: 0.12, f: 700, f2: 250, q: 1 });
        break;
      case 'unlock':
        this.arp(t, ['C5', 'G5', 'C6', 'G6', 'C7'], 0.09, { type: 'triangle', dur: 0.3, vol: 0.12, rev: 0.8 });
        this.tone(t + 0.45, { type: 'square', f: nf('C6'), dur: 0.5, vol: 0.05, cutoff: 3000, rev: 0.5 });
        break;
      default:
        break;
    }
  }

  // ---------- music ----------
  compile(name) {
    const tr = TRACKS[name];
    if (!tr) return null;
    const bars = tr.chords.length;
    const steps = bars * 16;
    const ev = Array.from({ length: steps }, () => []);
    const mel = parseMelody(tr.melody);
    for (const n of mel.notes) ev[Math.floor(n.step) % steps].push({ ch: 'lead', m: n.midi, len: n.len, off: n.step % 1 });
    tr.chords.forEach((cname, bar) => {
      const cn = chordNotes(cname, 2);
      const root = cn[0], fifth = cn[2] ?? cn[0] + 7, third = cn[1];
      const b0 = bar * 16;
      const add = (s, m, len, ch = 'bass') => ev[b0 + s].push({ ch, m, len, off: 0 });
      switch (tr.bass) {
        case 'pop':
          add(0, root, 3); add(4, root + 12, 2); add(6, root, 2); add(8, fifth, 3); add(12, root + 12, 2); add(14, fifth, 2);
          break;
        case 'drive':
          for (let s = 0; s < 16; s += 2) add(s, s % 8 === 6 ? root + 12 : root, 1.6);
          break;
        case 'walk':
          add(0, root, 4); add(4, third, 4); add(8, fifth, 4); add(12, third + 12 > fifth ? fifth + 2 : third + 12, 4);
          break;
        case 'sparse':
          add(0, root, 8); add(8, fifth, 8);
          break;
      }
      if (tr.arp) {
        const up = chordNotes(cname, 4);
        const seq = [0, 1, 2, 1];
        for (let s = 0; s < 16; s += 2) add(s, up[seq[(s / 2) % 4] % up.length], 1.5, 'arp');
      }
      // pad: long chord at start of each bar (soft)
      const pad = chordNotes(cname, 3);
      for (const m of pad) add(0, m, 15, 'pad');
    });
    const dr = DRUMS[tr.drums] || DRUMS.none;
    for (let bar = 0; bar < bars; bar++) {
      const pat = dr[(bar % 4 === 3 ? 1 : 0) % dr.length];
      for (let s = 0; s < 16; s++) {
        const c = pat[s];
        if (c && c !== '.') ev[bar * 16 + s].push({ ch: 'drum', d: c });
      }
    }
    return { ...tr, ev, steps, stepDur: 60 / tr.bpm / 4 };
  }

  playMusic(name) {
    if (!this.ctx) {
      this.pendingTrack = name;
      return;
    }
    if (this.current && this.current.name === name) return;
    this.stopMusic();
    const song = this.compile(name);
    if (!song) return;
    const bus = this.ctx.createGain();
    bus.gain.value = 0.0001;
    bus.gain.exponentialRampToValueAtTime(1, this.ctx.currentTime + 0.6);
    bus.connect(this.music);
    const revS = this.ctx.createGain();
    revS.gain.value = 0.25;
    bus.connect(revS).connect(this.revSend);
    const cur = { name, song, bus, step: 0, next: this.ctx.currentTime + 0.1, timer: null };
    cur.timer = setInterval(() => this.schedule(cur), 25);
    this.current = cur;
  }

  stopMusic() {
    const cur = this.current;
    if (!cur) return;
    clearInterval(cur.timer);
    const t = this.ctx.currentTime;
    cur.bus.gain.cancelScheduledValues(t);
    cur.bus.gain.setValueAtTime(cur.bus.gain.value, t);
    cur.bus.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    setTimeout(() => cur.bus.disconnect(), 600);
    this.current = null;
  }

  schedule(cur) {
    const c = this.ctx;
    if (!c || c.state !== 'running') {
      cur.next = c ? c.currentTime + 0.1 : 0;
      return;
    }
    const s = cur.song;
    if (cur.next < c.currentTime - 0.2) cur.next = c.currentTime + 0.05; // recover after a stall
    while (cur.next < c.currentTime + 0.12) {
      const list = s.ev[cur.step % s.steps];
      for (const e of list) this.musicNote(cur, e, cur.next + (e.off || 0) * s.stepDur, s);
      cur.next += s.stepDur;
      cur.step++;
    }
  }

  musicNote(cur, e, t, s) {
    const dest = cur.bus;
    const sd = s.stepDur;
    if (e.ch === 'drum') {
      for (const d of e.d) {
        if (d === 'k') {
          this.tone(t, { type: 'sine', f: 150, f2: 42, glide: 0.12, dur: 0.16, vol: 0.34, dest });
        } else if (d === 's') {
          this.noise(t, { dur: 0.13, vol: 0.18, f: 1900, q: 0.7, dest });
          this.tone(t, { type: 'triangle', f: 210, f2: 150, dur: 0.07, vol: 0.12, dest });
        } else if (d === 'h') {
          this.noise(t, { dur: 0.03, vol: 0.06, type: 'highpass', f: 7500, q: 0.7, dest });
        } else if (d === 'o') {
          this.noise(t, { dur: 0.14, vol: 0.06, type: 'highpass', f: 6500, q: 0.7, dest });
        }
      }
      return;
    }
    const f = mtof(e.m);
    const dur = e.len * sd;
    if (e.ch === 'lead') {
      if (s.lead === 'bell') {
        this.tone(t, { type: 'sine', f, dur: Math.max(0.25, dur), vol: 0.12, a: 0.004, r: 0.6, dest });
        this.tone(t, { type: 'sine', f: f * 2.01, dur: 0.15, vol: 0.04, a: 0.002, r: 0.3, dest });
      } else {
        this.tone(t, { type: s.lead, f, dur: dur * 0.92, vol: s.lead === 'sawtooth' ? 0.05 : 0.055, a: 0.01, r: 0.06, cutoff: s.cutoff, dest });
        this.tone(t, { type: 'triangle', f, dur: dur * 0.92, vol: 0.05, a: 0.01, r: 0.08, dest, detune: 6 });
      }
    } else if (e.ch === 'bass') {
      this.tone(t, { type: 'triangle', f, dur: dur * 0.9, vol: 0.2, a: 0.008, r: 0.04, dest });
      this.tone(t, { type: 'sawtooth', f, dur: dur * 0.8, vol: 0.035, a: 0.008, r: 0.03, cutoff: 600, dest });
    } else if (e.ch === 'arp') {
      this.tone(t, { type: 'triangle', f, dur: dur * 0.7, vol: 0.035, a: 0.004, r: 0.05, dest });
    } else if (e.ch === 'pad') {
      this.tone(t, { type: 'sine', f, dur, vol: 0.022, a: 0.25, r: 0.3, dest });
    }
  }

  // ---------- speech (read questions aloud) ----------
  speak(text) {
    if (!('speechSynthesis' in window)) return;
    try {
      const spoken = String(text)
        .replace(/\?(?=\s*[+×÷−=])/g, ' what ')
        .replace(/([+×÷−=])\s*\?/g, '$1 what ')
        .replace(/×/g, ' times ')
        .replace(/÷/g, ' divided by ')
        .replace(/−/g, ' minus ')
        .replace(/\+/g, ' plus ')
        .replace(/=/g, ' equals ')
        .replace(/_+/g, ' blank ');
      speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(spoken);
      if (!this.voice) {
        const vs = speechSynthesis.getVoices();
        this.voice = vs.find((v) => /en-GB/i.test(v.lang) && /female|Serena|Kate|Martha|Google UK English Female/i.test(v.name)) || vs.find((v) => /en-GB/i.test(v.lang)) || vs.find((v) => /^en/i.test(v.lang)) || null;
      }
      if (this.voice) u.voice = this.voice;
      u.rate = 0.92;
      u.pitch = 1.1;
      speechSynthesis.speak(u);
    } catch (e) {
      /* ignore */
    }
  }
}

export const audio = new AudioEngine();
